import BaseStepHandler from './BaseStepHandler';
import { Gradient } from 'fabric';

/**
 * Handler for card number step type
 * Formats two numbers as "{Number#1} of {Number#2}" with fixed styling
 */
export default class CardNumberStepHandler extends BaseStepHandler {
    /**
     * Create a new CardNumberStepHandler
     */
    constructor(stepId, stepDefinition, canvas, stepManager, side) {
        super(stepId, stepDefinition, canvas, stepManager, side);
        
        // Define default format template
        this.format = '{0} of {1}';
        
        // Set fixed styling properties - these cannot be changed by the user
        this.fixedStyling = {
            fontFamily: 'birds_of_paradise',
            // Define gradient colors from yellow to orange
            gradientColors: {
                start: '#f8be12', // Yellow
                end: '#ed9203'    // Orange
            }
        };
    }

    /**
     * Initialize card number elements
     */
    initialize() {
        super.initialize();

        // Get the main element ID from definition
        const elementId = this.definition.elements?.[0];
        if (!elementId) return this;

        // Get element from canvas
        let textElement = this.canvas.getElement(elementId);
        if (!textElement) return this;

        // Store element data from template
        this.elementData = this.canvas.getElementData(elementId);

        // Store reference to the element
        this.elements.set(elementId, textElement);

        return this;
    }

    /**
     * Process template - handles both new templates and edit mode
     */
    processTemplate() {
        const stepData = this.getStepData();
        const elementId = this.definition.elements?.[0];
        if (!elementId) return this;

        const textElement = this.elements.get(elementId);
        if (!textElement) return this;

        console.log('🔢 [CARD_NUMBER_PROCESS] stepData:', stepData);

        // If we have step data (from edit mode), use it
        // BUT also check if we need to restore position from element
        if (stepData && Object.keys(stepData).length > 0) {
            // Check if position data exists in stepData, if not get from element
            const completeStepData = {
                ...stepData,
                left: stepData.left !== undefined ? stepData.left : textElement.left,
                top: stepData.top !== undefined ? stepData.top : textElement.top,
                scaleX: stepData.scaleX !== undefined ? stepData.scaleX : textElement.scaleX,
                scaleY: stepData.scaleY !== undefined ? stepData.scaleY : textElement.scaleY
            };

            console.log('🔢 [CARD_NUMBER_PROCESS] Complete stepData with position:', completeStepData);

            // Update stepData with complete info including position
            this.setStepData(completeStepData, false);

            this.update(completeStepData);
            return this;
        }

        // Otherwise, check if template has existing data (edit mode from template)
        const elementData = this.canvas.getElementData(elementId);
        if (elementData?.data) {
            // Extract data from template
            const initialStepData = {
                number1: elementData.data.number1 || 'One',
                number2: elementData.data.number2 || 'One',
                enabled: elementData.data.enabled === true,
                // Preserve position and scale from element data or element itself
                left: elementData.data.left || textElement.left,
                top: elementData.data.top || textElement.top,
                scaleX: elementData.data.scaleX || textElement.scaleX,
                scaleY: elementData.data.scaleY || textElement.scaleY
            };

            console.log('🔢 [CARD_NUMBER_PROCESS] Initial stepData from template:', initialStepData);

            // Update with initial step data
            this.setStepData(initialStepData, false);

            // Apply settings to the element
            this.update(initialStepData);
        } else {
            // New template - use defaults
            const defaultStepData = {
                number1: 'One',
                number2: 'One',
                enabled: false,
                left: textElement.left,
                top: textElement.top,
                scaleX: textElement.scaleX,
                scaleY: textElement.scaleY
            };

            console.log('🔢 [CARD_NUMBER_PROCESS] Default stepData:', defaultStepData);

            this.setStepData(defaultStepData, false);
            this.update(defaultStepData);
        }

        return this;
    }
    
    /**
     * Update text element with step data
     * @param {object} stepData - Step data
     */
    update(stepData) {
        super.update(stepData);
        
        // Get main element
        const elementId = this.definition.elements?.[0];
        if (!elementId) return this;
        
        const element = this.elements.get(elementId);
        if (!element) return this;
        
        // Format the numbers
        let formattedText = this.format;
        
        // Replace placeholders with actual numbers
        if (stepData.number1 !== undefined) {
            formattedText = formattedText.replace('{0}', stepData.number1);
        }
        
        if (stepData.number2 !== undefined) {
            formattedText = formattedText.replace('{1}', stepData.number2);
        }
        
        // Update the text element
        element.set({
            text: formattedText,
            // Apply fixed styling (cannot be changed by user)
            fontFamily: this.fixedStyling.fontFamily,
            // Preserve position from stepData or element
            left: stepData.left !== undefined ? stepData.left : element.left,
            top: stepData.top !== undefined ? stepData.top : element.top,
            // Preserve scale from stepData or element
            scaleX: stepData.scaleX !== undefined ? stepData.scaleX : element.scaleX,
            scaleY: stepData.scaleY !== undefined ? stepData.scaleY : element.scaleY,
            // Show/hide based on enabled state
            visible: stepData.enabled === true,
            cornerSize: 50,
            borderScaleFactor:10,
            padding: 10,
            evented: true,
            selectable: true,
            hasControls: true
        });
        
        // Apply gradient to text
        this.applyGradientToText(element);
        
        this.canvas.canvas.requestRenderAll();
        
        return this;
    }
    
    /**
     * Apply gradient to text element
     * @param {object} element - Fabric text element
     */
    applyGradientToText(element) {
        // Create a vertical gradient with a hard transition in the middle
        const gradient = new Gradient({
            type: 'linear',
            coords: {
                x1: 0,
                y1: 0,            // Top of element
                x2: 0,            // Same x-coordinate for vertical gradient
                y2: element.height // Bottom of element
            },
            colorStops: [
                { offset: 0, color: this.fixedStyling.gradientColors.start },  // Top color
                { offset: 0.6, color: this.fixedStyling.gradientColors.start }, // Middle-top (same color)
                { offset: 0.6001, color: this.fixedStyling.gradientColors.end }, // Middle-bottom (different color)
                { offset: 1, color: this.fixedStyling.gradientColors.end }     // Bottom color
            ]
        });
        
        // Apply the gradient to the text
        element.set('fill', gradient);
    }
    
    /**
     * Activate the step
     */
    activate() {
        super.activate();
        
        // When activated, store current position and scale
        const elementId = this.definition.elements?.[0];
        if (!elementId) return this;
        
        const element = this.elements.get(elementId);
        if (!element) return this;
        
        // Update step data with current position and scale
        const stepData = this.getStepData() || {};
        if (stepData) {
            stepData.left = element.left;
            stepData.top = element.top;
            stepData.scaleX = element.scaleX;
            stepData.scaleY = element.scaleY;
            this.setStepData(stepData, false);
        }
        
        return this;
    }
    
    /**
     * Handle element movement or scaling
     * Overriding to capture position changes
     */
    handleElementChange(elementId, changes) {
        const stepData = this.getStepData() || {};
        
        // Update position and scale in step data
        if (changes.left !== undefined) stepData.left = changes.left;
        if (changes.top !== undefined) stepData.top = changes.top;
        if (changes.scaleX !== undefined) stepData.scaleX = changes.scaleX;
        if (changes.scaleY !== undefined) stepData.scaleY = changes.scaleY;
        
        this.setStepData(stepData, false);
    }
    
    /**
     * Render panel content
     * Includes toggle button for showing/hiding the card number
     */
    renderPanelContent() {
        const stepData = this.getStepData() || {};
        const gradientStyle = `background: linear-gradient(to right, ${this.fixedStyling.gradientColors.start}, ${this.fixedStyling.gradientColors.end}); -webkit-background-clip: text; -webkit-text-fill-color: transparent;`;
        
        return `
            <div class="stc-card-number-field">
                <div class="stc-toggle-field">
                    <label class="stc-switch">
                        <input type="checkbox" class="stc-card-number-toggle" ${stepData.enabled === true ? 'checked' : ''}>
                        <span class="stc-slider"></span>
                    </label>
                    <span class="stc-toggle-label">Show Card Number</span>
                </div>
                
                <div class="stc-card-number-preview ${stepData.enabled !== true ? 'stc-card-number-disabled' : ''}">
                    <span class="stc-card-number-label">Preview: </span>
                    <span class="stc-card-number-formatted" 
                          style="font-family: ${this.fixedStyling.fontFamily}; ${gradientStyle}">
                        ${stepData.number1 || 'One'} of ${stepData.number2 || 'One'} 
                    </span>
                </div>
                
                <div class="stc-card-number-inputs ${stepData.enabled !== true ? 'stc-card-number-disabled' : ''}">
                    <div class="stc-card-number-input-group">
                        <label for="card-number-1">Number #1:</label>
                        <input type="text" 
                               id="card-number-1" 
                               class="stc-input stc-card-number-input" 
                               value="${stepData.number1 || 'One'}"
                               ${stepData.enabled !== true ? 'disabled' : ''}>
                    </div>
                    
                    <div class="stc-card-number-input-group">
                        <label for="card-number-2">Number #2:</label>
                        <input type="text" 
                               id="card-number-2" 
                               class="stc-input stc-card-number-input" 
                               value="${stepData.number2 || 'One'}"
                               ${stepData.enabled !== true ? 'disabled' : ''}>
                    </div>
                </div>
                
                <div class="stc-step-controls">
                    <button type="button" class="stc-clear-step">
                        Reset
                    </button>
                </div>
            </div>
        `;
    }
    
    /**
     * Bind panel events
     */
    bindPanelEvents() {
        if (!this.panelElement) return;
        
        // Get UI elements
        const toggleCheckbox = this.panelElement.querySelector('.stc-card-number-toggle');
        const number1Input = this.panelElement.querySelector('#card-number-1');
        const number2Input = this.panelElement.querySelector('#card-number-2');
        const previewEl = this.panelElement.querySelector('.stc-card-number-formatted');
        const previewContainer = this.panelElement.querySelector('.stc-card-number-preview');
        const inputsContainer = this.panelElement.querySelector('.stc-card-number-inputs');
        const clearButton = this.panelElement.querySelector('.stc-clear-step');
        
        // Bind toggle checkbox
        if (toggleCheckbox) {
            this.addEventListener(toggleCheckbox, 'change', () => {
                const stepData = this.getStepData() || {};
                stepData.enabled = toggleCheckbox.checked;
                
                // Enable/disable inputs
                if (number1Input) number1Input.disabled = !toggleCheckbox.checked;
                if (number2Input) number2Input.disabled = !toggleCheckbox.checked;
                
                // Update preview styling
                if (previewContainer) {
                    previewContainer.classList.toggle('stc-card-number-disabled', !toggleCheckbox.checked);
                }
                
                if (inputsContainer) {
                    inputsContainer.classList.toggle('stc-card-number-disabled', !toggleCheckbox.checked);
                }
                
                this.setStepData(stepData);
            });
        }
        
        // Bind number 1 input event
        if (number1Input) {
            this.addEventListener(number1Input, 'input', () => {
                const stepData = this.getStepData() || {};
                stepData.number1 = number1Input.value;
                
                // Update preview
                if (previewEl) {
                    previewEl.textContent = `${stepData.number1 || 'One'} of ${stepData.number2 || 'One'}`; 
                }
                
                this.setStepData(stepData);
            });
        }
        
        // Bind number 2 input event
        if (number2Input) {
            this.addEventListener(number2Input, 'input', () => {
                const stepData = this.getStepData() || {};
                stepData.number2 = number2Input.value;
                
                // Update preview
                if (previewEl) {
                    previewEl.textContent = `${stepData.number1 || 'One'} of ${stepData.number2 || 'One'}`;
                }
                
                this.setStepData(stepData);
            });
        }
        
        // Bind clear button event
        if (clearButton) {
            this.addEventListener(clearButton, 'click', () => {
                this.clear();
            });
        }
    }
    
    /**
     * Get export data for card number step
     * @returns {object} Export data
     */
    getStepExportData() {
        const baseData = super.getStepExportData();
        const stepData = this.getStepData() || {};
        
        return {
            ...baseData,
            content: {
                number1: stepData.number1 || 'One',
                number2: stepData.number2 || 'One',
                enabled: stepData.enabled !== false,
                formattedText: this.format
                    .replace('{0}', stepData.number1 || 'One')
                    .replace('{1}', stepData.number2 || 'One'),
                // Include fixed styling properties
                fontFamily: this.fixedStyling.fontFamily,
                // Include gradient colors
                gradientColors: this.fixedStyling.gradientColors
            }
        };
    }
    
    /**
     * Get image reference for export
     * Override to return null since this isn't an image handler
     */
    getImageReferenceForExport() {
        return null;
    }
    
    /**
     * Step-specific reset logic
     */
    onReset() {
        const elementId = this.definition.elements?.[0];
        if (!elementId) return;
        
        const element = this.getElement(elementId);
        if (!element) return;
        
        // Reset to default values using 'One' instead of numbers
        const defaultText = this.format.replace('{0}', 'One').replace('{1}', 'One');
        
        element.set({
            text: defaultText,
            // Keep fixed styling
            fontFamily: this.fixedStyling.fontFamily,
            // Make visible on reset
            visible: true,
            borderScaleFactor:10,
            cornerSize: 50,
        });
        
        // Apply gradient to text
        this.applyGradientToText(element);
        
        // Reset step data
        this.setStepData({
            number1: 'One',
            number2: 'One',
            enabled: true,
            // Preserve position and scale
            left: element.left,
            top: element.top,
            scaleX: element.scaleX,
            scaleY: element.scaleY
        });
    }
    
    /**
     * Check if step is complete
     * @returns {boolean} True if step is complete
     */
    isComplete() {
        const stepData = this.getStepData() || {};
        // Step is complete if it has both values and is enabled
        return stepData.number1 && stepData.number2 && stepData.enabled !== false;
    }
}